import { z } from 'zod'
import { imageAssetRefSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'

/**
 * A cover photograph write value: a storage key (after upload), an external
 * http(s) URL, or an inline data URL when object storage is off — the same three
 * forms every other image on the wire takes.
 *
 * Venues, events and vendors each have one. It is the photograph the design
 * system's hero and image-forward cards are built around, so it is the difference
 * between a listing that looks finished and one that shows a placeholder drawing.
 */
export const coverImageSchema = imageAssetRefSchema

export type CoverImageInput = z.infer<typeof coverImageSchema>

/**
 * Set or clear one entity's cover photograph.
 *
 * Its own route, rather than a field on the entity's update input, wherever the
 * two need different permissions: a venue admin may change their venue's
 * photograph without being able to rename the venue or move its address, which
 * are tenant-level decisions.
 */
export const setCoverImageInputSchema = z
  .object({
    /**
     * The new cover, or null to remove it. **Omitted leaves it as it was.**
     *
     * Optional because it was required, and the focal-point picker therefore had
     * to send something — so it sent back the signed URL it had been given to
     * display. That URL is not a storage reference: stored, it expires within
     * the hour and the photograph is gone for good. Nothing should have to name
     * an image in order to say where to crop it.
     */
    coverImageUrl: coverImageSchema.nullable().optional(),
    /**
     * Where the subject of the cover is. Omitted leaves it as it was, which
     * matters because the picker sends a point without re-sending the image.
     */
    coverFocalPoint: focalPointSchema.nullable().optional(),
  })
  .strict()
  // Both optional is what lets each be sent without the other; both *omitted* is
  // a request that says nothing, and answering it 200 having written nothing
  // tells a caller its write succeeded when nothing of the kind happened.
  .refine((input) => input.coverImageUrl !== undefined || input.coverFocalPoint !== undefined, {
    message: 'Give coverImageUrl, coverFocalPoint, or both',
  })

export type SetCoverImageInput = z.infer<typeof setCoverImageInputSchema>
